import { login, logout } from '../auth/store/auth.reducer';
import type { AppDispatch } from '../store';
import { store } from '../store';
import { getWindow } from '../utils/window.utils';
import { logError } from './store/element.thunk';

export class ButtonService {
  selectors: string[];
  readonly IGNORE_SELECTOR = ':not(single-ignore)';

  // Lazy getter to access dispatch - called only when needed, not at import time
  private get dispatch(): AppDispatch {
    return store.dispatch as AppDispatch;
  }

  private haltEvent = ($event) => {
    $event.preventDefault();
    $event.stopPropagation();
    $event.stopImmediatePropagation();
  };

  bindLoginFunction(): HTMLElement {
    return this.bindFunction('.single-login-button', (event) => {
      this.dispatch(login());
    });
  }

  bindLogoutFunction(): HTMLElement {
    return this.bindFunction('.single-logout-button', (_) => this.dispatch(logout()));
  }

  toggleVisibility(
    element: HTMLElement | null | undefined,
    value: boolean,
    action?: string
  ): Element | undefined {
    if (!element) {
      return undefined;
    }
    element.hidden = !value;
    if (value) {
      element.classList.remove('single-hidden');
    } else {
      element.classList.add('single-hidden');
    }
    return element;
  }

  getAllElements(className: string, parent: ParentNode = document): HTMLElement[] {
    const elements = parent.querySelectorAll(className);
    if (!elements || elements.length === 0) {
      return [];
    }
    return Array.from(elements) as HTMLElement[];
  }

  getAllElementsWithMultipleSelectors(
    selectors: string[],
    parent: ParentNode = document
  ): HTMLElement[] {
    let results: HTMLElement[] = [];
    selectors.forEach((selector) => {
      const elements = this.getAllElements(selector, parent);
      if (!!elements && elements.length > 0) {
        results = results.concat(Array.from(elements) as HTMLElement[]);
      }
    });
    return results;
  }

  getFirstElement(className: string, parent: ParentNode = document): HTMLElement | undefined {
    const elements = parent.querySelectorAll(className);
    if (!elements || elements.length === 0) {
      return undefined;
    }
    const element = elements[0];
    return element as HTMLElement;
  }

  getFirstElementWithMultipleSelectors(
    selectors: string[],
    parent: ParentNode = document
  ): HTMLElement | undefined {
    for (const selector of selectors) {
      const section = this.getFirstElement(selector, parent);
      if (!section) {
        continue;
      }
      return section;
    }
    return undefined;
  }

  renderContent(element: HTMLElement, content: string): void {
    element.innerHTML = content;
    this.getAllElements('script', element).forEach((script) => {
      const scriptClone = document.createElement('script');
      if (!!script.innerHTML) {
        scriptClone.text = script.innerHTML;
      }
      if (!!script.getAttribute('src')) {
        scriptClone.setAttribute('src', script.getAttribute('src'));
      }
      script.replaceWith(scriptClone);
    });
    this.getAllElements('.single-accessibility', element).forEach((accessibleElement) => {
      accessibleElement.tabIndex = 0;
      accessibleElement.addEventListener('keydown', (event) => {
        let handled = false;
        if (event.code) {
          handled =
            event.code === 'Enter' || event.code === 'Space' || event.code === 'NumpadEnter';
        } else if (event.keyCode) {
          handled = event.keyCode === 13 || event.keyCode === 32;
        }
        if (handled) {
          (event.target as HTMLElement).click();
          event.preventDefault();
        }
      });
    });
    this.getAllElements('#AddToCart', element).forEach((addToCart) =>
      this.toggleVisibility(addToCart, false)
    );
    this.toggleVisibility(element, true);
  }

  clearContent(element: HTMLElement): void {
    element.innerHTML = '';
  }

  bindFunction(selector: string, func: (event) => void): HTMLElement {
    const elements = document.querySelectorAll(selector);
    if (!elements || elements.length === 0) {
      return undefined;
    }

    elements.forEach((e, i, p) => {
      e.addEventListener('click', ($event) => {
        this.haltEvent($event);
        func($event);
      });
    });

    return elements[0] as HTMLElement;
  }

  bindFormSubmit(selector: string, func: (event) => void): void {
    const elements = document.querySelectorAll(selector);
    if (!elements || elements.length === 0) {
      return undefined;
    }

    elements.forEach((e, i, p) => {
      e.addEventListener('submit', ($event) => {
        this.haltEvent($event);
        func($event);
      });
    });
  }

  bindFunctionToCartDelete(func: (event) => void): void {
    const selectors = ['cart-remove-button'].concat(
      getWindow().SINGLE_CUSTOM_CART_ITEM_DELETE_SELECTORS || []
    );
    const deletes = this.getAllElementsWithMultipleSelectors(selectors);
    if (!!deletes) {
      deletes.forEach((element) => {
        const clone = this.cloneAsTag(element, 'div');
        const link = this.getFirstElement('a', clone);
        if (!!link) {
          link.removeAttribute('href');
        }
        clone.addEventListener('click', ($event) => {
          this.haltEvent($event);
          func($event);
        });
        element.replaceWith(clone);
      });
    }
  }

  bindFunctionToCartUpdate(func: (event) => void): void {
    const selectors = ['.quantity__input'].concat(
      getWindow().SINGLE_CUSTOM_CART_ITEM_QUANTITY_SELECTORS || []
    );
    const inputs = this.getAllElementsWithMultipleSelectors(selectors);
    if (!!inputs) {
      inputs.forEach((element) => {
        element.addEventListener('change', ($event) => {
          this.haltEvent($event);
          func($event);
        });
      });
    }
  }

  bindFunctionToCheckoutButton(func: () => void): HTMLElement {
    const form = document.querySelector('form[action="/cart"]') as HTMLElement;
    const selectors = [
      'input[type="submit"]', // Debut
      'button[id="checkout"]', // Dawn
      'button[type="submit"]',
    ].concat(getWindow().SINGLE_CUSTOM_CHECKOUT_BUTTON_SELECTORS || []);
    let found;
    selectors
      .map((selector) => {
        const elem = form.querySelector(selector);
        if (!!elem) {
          return elem;
        }
        return document.querySelector(selector);
      })
      .filter((button) => !!button)
      .forEach((element) => {
        const clone = element.cloneNode(true);
        clone.addEventListener('click', ($event) => {
          this.haltEvent($event);
          func();
        });
        element.replaceWith(clone);
        found = clone;
      });
    if (!found) {
      this.dispatch(
        logError(('[ButtonService]', 'unable to bind to checkout button', selectors).join(' '))
      );
    }
    return found;
  }

  bindCartItems(): HTMLElement | undefined {
    const selectors = ['cart-items', '.cart__item-list', '[data-cart-line-items]'].concat(
      getWindow().SINGLE_CUSTOM_CART_ITEMS_SECTION_SELECTORS || []
    );
    const elem = this.getFirstElementWithMultipleSelectors(selectors);
    if (!!elem) {
      return elem;
    }
    this.dispatch(
      logError(('[ButtonService]', 'unable to bind to cart items section', selectors).join(' '))
    );
    return undefined;
  }

  bindSearchFunction(func: () => void, parent: ParentNode = document): HTMLElement {
    const contentQuery = this.getFirstElement('#contentQuery', parent) as HTMLInputElement;
    if (!!contentQuery) {
      contentQuery.addEventListener('focus', () => {
        if (contentQuery.value === 'Search...') {
          contentQuery.value = '';
        }
      });
      contentQuery.addEventListener('keyup', ({ key }) => {
        if (key === 'Enter') {
          func();
        }
      });
    }

    const contentQuerySearch = this.getFirstElement('#contentQuerySearch', parent);
    if (!!contentQuerySearch) {
      contentQuerySearch.addEventListener('click', () => {
        func();
      });
    }

    const contentQueryClear = this.getFirstElement('#contentQueryClear', parent);
    if (!!contentQueryClear) {
      contentQueryClear.addEventListener('click', () => {
        if (!!contentQuery) {
          contentQuery.value = '';
        }
        func();
      });
    }

    return contentQuery;
  }

  private cloneStyling(target: HTMLElement, source: Element): void {
    const style = window.getComputedStyle(source);
    for (let i = 0; i < style.length; i++) {
      const name = style.item(i);
      target.style.setProperty(name, style.getPropertyValue(name), style.getPropertyPriority(name));
    }
    for (let i = 0; i < target.children.length; i++) {
      const childClone = target.children.item(i);
      const childSource = source.children.item(i);
      this.cloneStyling(childClone as HTMLElement, childSource);
    }
  }

  private cloneAttributes(target: Element, source: Element): void {
    for (let i = 0; i < source.attributes.length; i++) {
      const attr = source.attributes.item(i);
      target.setAttribute(attr.name, attr.value);
    }
  }

  private cloneAsTag(source: HTMLElement, tagName: string): HTMLElement {
    const clone = document.createElement(tagName);
    clone.innerHTML = source.innerHTML;
    this.cloneStyling(clone, source);
    this.cloneAttributes(clone, source);
    Object.assign(clone.dataset, source.dataset);
    return clone;
  }
}
